import { useCallback } from "react";
import { useParams } from "react-router-dom";
import NotesTable from "@/features/global/components/NotesTable";
import TasksTable from "@/features/global/components/TasksTable";
import { getNoteParentLink } from "@/features/global/components/NotesTable/noteHelpers";
import {
  DOMAIN,
  PERMISSION,
  useCanManagePersonNotes,
  useCanManageTasksBool,
  useHasCapabilityBool
} from "@/hooks/useCapabilities";
import { PERSON_NOTES_QUERY } from "@/pages/people/[pgaId]/person/notes/queries";
import { useApplicationContext } from "./useApplicationContext";
import type { CRMNote } from "@/types";

export default function ApplicationNotesPage() {
  const { applicationId } = useParams();
  const { application } = useApplicationContext();
  const canManageNotes = useCanManagePersonNotes();
  const pgaId = application.person.pgaId;
  const canViewPersonNotes = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.viewPersonNotes
  });

  const canManageTasks = useCanManageTasksBool();
  const showTasks = canManageTasks;

  const getParentLink = useCallback((note: CRMNote): string | null => getNoteParentLink(note, { pgaId }), [pgaId]);

  const noteEditing = {
    enabled: canManageNotes,
    notableId: applicationId || "",
    notableType: "MEMBERSHIP_APPLICATION" as const,
    refetchQueries: [PERSON_NOTES_QUERY],
    pgaId,
    objectType: "application"
  };

  return (
    <div className="pb-6">
      {canViewPersonNotes && (
        <NotesTable
          variant="top-level"
          notesQuery={PERSON_NOTES_QUERY}
          queryVariables={{ pgaId }}
          notableTypeFilter={["MEMBERSHIP_APPLICATION"]}
          notableIdFilter={applicationId}
          tableHeading="Application Notes"
          tableBody="View all notes created for this application. External notes have been sent to the applicant as part of the review process."
          getParentLink={getParentLink}
          noteEditing={noteEditing}
          addNoteVariant="outlined"
          hiddenFilters={["sourceType"]}
        />
      )}
      {showTasks && (
        <TasksTable
          referenceableId={applicationId || ""}
          referenceableType="MEMBERSHIP_APPLICATION"
          tableHeading="Application Tasks"
          tableBody="View and manage tasks for this application"
          hideControls
        />
      )}
    </div>
  );
}
